import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { Subscription } from "rxjs";

import { IncidenciasService } from "src/app/incidencias/incidencias.service";
import { IncidenciaDeEtapa, MAXIMO_BYTES_PETICION_PRUEBA } from "../etapas-versiones.model";
import { EtapasVersionesService } from "../etapas-versiones.service";
import {
  LARGO_DESCRIPCION_INCIDENCIA,
  LARGO_TITULO_INCIDENCIA,
  RespuestaAltaIncidencia,
  clasePrioridad,
  incidenciaDeBean,
  incidenciaNueva,
  nombreEvidenciaIncidencia,
  rechazoDelAlta,
} from "../incidencias-prueba.util";
import { cierrePorTeclaOFondo, configDialogoPruebas } from "../pruebas-dialogo";
import { lineaArchivo } from "../pruebas-version.util";

/** Lo que recibe «Crear incidencia»: dónde se detectó. */
export interface DatosIncidenciaRapida {
  idProyecto: string;
  idEtapa: string;
  idVersion: string;
  nombreProyecto?: string | null;
  nombreEtapa?: string | null;
  /** «V3-13-08-2026 · Vigente». */
  etiquetaVersion: string;
  /** «Regresión 2 · 14/08/2026» o «Ejecución nueva». */
  etiquetaEjecucion: string;
}

/** Lo que da al cerrar: la incidencia creada (para vincularla) y si su evidencia no se pudo subir. */
export interface ResultadoIncidenciaRapida {
  incidencia: IncidenciaDeEtapa;
  /** null si no llevaba evidencia o se subió; el aviso si falló (la incidencia ya existe). */
  avisoEvidencia: string | null;
}

interface ControlesIncidencia {
  titulo: FormControl<string>;
  prioridad: FormControl<string | null>;
  tipo: FormControl<string | null>;
  descripcion: FormControl<string>;
}

const ID_TITULO = "t2i-titulo";
/** Los catálogos del alta, si no se pueden leer (los valores de hoy). */
const PRIORIDADES_RESPALDO: readonly string[] = ["ALTA", "MEDIA", "BAJA"];
const TIPOS_RESPALDO: readonly string[] = ["FUNCIONAL", "ORTOGRAFICO"];
/** Lo mismo que acepta el alta de incidencias, sin lo que no sea evidencia de un defecto. */
const ACEPTA = "image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.log,.zip";

/** Abre «Crear incidencia» encima de T2. */
export function abrirIncidenciaRapida(
  dialog: MatDialog,
  datos: DatosIncidenciaRapida,
): MatDialogRef<IncidenciaRapidaComponent, ResultadoIncidenciaRapida> {
  return dialog.open<IncidenciaRapidaComponent, DatosIncidenciaRapida, ResultadoIncidenciaRapida>(
    IncidenciaRapidaComponent,
    configDialogoPruebas(datos, ID_TITULO, "760px"),
  );
}

/**
 * «Crear incidencia» desde T2 (plan de etapas y versiones §5.7, maqueta
 * T2i): un diálogo corto con los obligatorios del alta (título, prioridad,
 * tipo y descripción) que llama al alta de siempre (guardarIncidenciaQA.do)
 * con la versión que se está probando como la detectada. Nace en ABIERTO y
 * queda registrada aunque después no se guarde la ejecución: es un defecto
 * real. La evidencia es opcional y se sube después, como en el alta; si
 * falla, la incidencia ya existe y se avisa.
 */
@Component({
  selector: "app-incidencia-rapida",
  standalone: true,
  templateUrl: "./incidencia-rapida.component.html",
  styleUrls: ["../pruebas-dialogo.css", "./incidencia-rapida.component.css"],
  imports: [CommonModule, ReactiveFormsModule, MatDialogContent, MatDialogActions, MatFormFieldModule, MatInputModule, MatSelectModule],
})
export class IncidenciaRapidaComponent implements OnInit, OnDestroy {
  readonly idTitulo = ID_TITULO;
  readonly largoTitulo = LARGO_TITULO_INCIDENCIA;
  readonly largoDescripcion = LARGO_DESCRIPCION_INCIDENCIA;
  readonly acepta = ACEPTA;
  readonly subtitulo: string;

  readonly formulario = new FormGroup<ControlesIncidencia>({
    titulo: new FormControl("", { nonNullable: true, validators: [Validators.required, Validators.maxLength(LARGO_TITULO_INCIDENCIA)] }),
    prioridad: new FormControl<string | null>(null, { validators: [Validators.required] }),
    tipo: new FormControl<string | null>(null, { validators: [Validators.required] }),
    descripcion: new FormControl("", {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(LARGO_DESCRIPCION_INCIDENCIA)],
    }),
  });

  /** Los catálogos del alta; mientras llegan (o si no llegan), los valores de hoy. */
  prioridades: readonly string[] = PRIORIDADES_RESPALDO;
  tipos: readonly string[] = TIPOS_RESPALDO;
  archivos: File[] = [];
  avisoArchivos = "";
  guardando = false;
  mensaje = "";

  private cerrado = false;
  private readonly subs: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly datos: DatosIncidenciaRapida,
    private readonly dialogRef: MatDialogRef<IncidenciaRapidaComponent, ResultadoIncidenciaRapida>,
    private readonly servicio: EtapasVersionesService,
    private readonly incidencias: IncidenciasService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.subtitulo = [datos?.nombreProyecto, datos?.nombreEtapa].map((p) => (p ?? "").trim()).filter(Boolean).join(" · ");
  }

  ngOnInit(): void {
    this.subs.push(
      ...cierrePorTeclaOFondo(this.dialogRef, () => this.cancelar()),
      this.incidencias.consultaPrioridad().subscribe({
        next: (respuesta) => {
          this.prioridades = valoresDeCatalogo(respuesta, "nombre", PRIORIDADES_RESPALDO);
          this.pinta();
        },
        error: () => this.pinta(),
      }),
      this.incidencias.consultaTipo().subscribe({
        next: (respuesta) => {
          this.tipos = valoresDeCatalogo(respuesta, "tipo", TIPOS_RESPALDO);
          this.pinta();
        },
        error: () => this.pinta(),
      }),
    );
  }

  ngOnDestroy(): void {
    this.cerrado = true;
    this.subs.forEach((s) => s.unsubscribe());
  }

  /* ============================ Lo que pinta la plantilla ============================ */

  largo(campo: "titulo" | "descripcion"): number {
    return this.formulario.controls[campo].value.length;
  }

  error(campo: keyof ControlesIncidencia, tipo: string): boolean {
    return this.formulario.controls[campo].hasError(tipo);
  }

  clasePrioridad(prioridad: string | null): string {
    return clasePrioridad(prioridad);
  }

  lineaArchivo(archivo: File): string {
    return lineaArchivo(archivo.name, archivo.size);
  }

  /* ============================ Evidencia ============================ */

  elegirArchivos(entrada: HTMLInputElement): void {
    this.agregar(Array.from(entrada.files ?? []));
    entrada.value = "";
  }

  soltar(evento: DragEvent): void {
    evento.preventDefault();
    this.agregar(Array.from(evento.dataTransfer?.files ?? []));
  }

  permitirSoltar(evento: DragEvent): void {
    evento.preventDefault();
  }

  quitarArchivo(indice: number): void {
    this.archivos = this.archivos.filter((_, i) => i !== indice);
    this.avisoArchivos = "";
  }

  /** Sin vacíos y hasta 50 MB entre todos (el tope de la petición). */
  private agregar(nuevos: File[]): void {
    const rechazos: string[] = [];
    let bytes = this.archivos.reduce((suma, archivo) => suma + archivo.size, 0);
    for (const archivo of nuevos) {
      if (archivo.size <= 0) {
        rechazos.push(`El archivo «${archivo.name}» está vacío.`);
      } else if (bytes + archivo.size > MAXIMO_BYTES_PETICION_PRUEBA) {
        rechazos.push(`La evidencia no puede pasar de 50 MB: «${archivo.name}» no se agregó.`);
      } else {
        bytes += archivo.size;
        this.archivos = [...this.archivos, archivo];
      }
    }
    this.avisoArchivos = rechazos.join(" ");
    this.pinta();
  }

  /* ============================ Guardar ============================ */

  crear(): void {
    if (this.guardando) {
      return;
    }
    this.formulario.markAllAsTouched();
    if (this.formulario.invalid) {
      this.mensaje = "Revisa los campos marcados.";
      this.pinta();
      return;
    }
    const peticion = incidenciaNueva(this.formulario.getRawValue(), this.datos);
    this.guardando = true;
    this.mensaje = "";
    this.formulario.disable({ emitEvent: false });
    this.pinta();
    this.subs.push(
      this.servicio.crearIncidencia(peticion).subscribe({
        next: (respuesta) => this.alCrear(respuesta),
        error: () => this.falla("No se pudo crear la incidencia: sin conexión con el servidor."),
      }),
    );
  }

  private alCrear(respuesta: RespuestaAltaIncidencia | null): void {
    const rechazo = rechazoDelAlta(respuesta);
    const incidencia = rechazo ? null : incidenciaDeBean(respuesta);
    if (!respuesta || !incidencia) {
      this.falla(rechazo ?? "No se pudo crear la incidencia.");
      return;
    }
    if (!this.archivos.length) {
      this.cierra({ incidencia, avisoEvidencia: null });
      return;
    }
    const datos = new FormData();
    this.archivos.forEach((archivo) => datos.append("file", archivo, nombreEvidenciaIncidencia(respuesta, archivo.name)));
    const folio = incidencia.folio === null ? "" : ` #${incidencia.folio}`;
    this.subs.push(
      this.incidencias.uploadEvidencia(datos).subscribe({
        complete: () => this.cierra({ incidencia, avisoEvidencia: null }),
        error: () =>
          this.cierra({
            incidencia,
            avisoEvidencia: `La incidencia${folio} se creó, pero no se pudo subir su evidencia; agrégala desde su detalle.`,
          }),
      }),
    );
  }

  private falla(texto: string): void {
    this.guardando = false;
    this.mensaje = texto;
    this.formulario.enable({ emitEvent: false });
    this.pinta();
  }

  /* ============================ Cerrar ============================ */

  cancelar(): void {
    if (this.guardando || this.cerrado) {
      return;
    }
    this.cerrado = true;
    this.dialogRef.close();
  }

  private cierra(resultado: ResultadoIncidenciaRapida): void {
    if (this.cerrado) {
      return;
    }
    this.cerrado = true;
    this.dialogRef.close(resultado);
  }

  /** La app corre sin zonas: lo que llega de una petición se pinta a mano. */
  private pinta(): void {
    if (!this.cerrado) {
      this.cdr.detectChanges();
    }
  }
}

/** Los valores de un catálogo del alta ([{nombre}] o [{tipo}]); el respaldo si no llegó nada. */
function valoresDeCatalogo(respuesta: unknown, campo: string, respaldo: readonly string[]): readonly string[] {
  const lista = Array.isArray(respuesta) ? (respuesta as Record<string, unknown>[]) : [];
  const valores = lista.map((fila) => fila?.[campo]).filter((valor): valor is string => typeof valor === "string" && !!valor.trim());
  return valores.length ? valores : respaldo;
}
